import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Card } from '@aimsos/ui';

import { deriveHitlFeatureTitle } from '../../../utils/hitlCardMeta.js';

/**
 * Localized short date-time for the gate's `created_at`, or '' when absent /
 * unparseable. Absolute (not relative) so an old, foreign card reads as stale
 * at a glance regardless of when the card is looked at.
 *
 * @param {string|null|undefined} iso
 * @param {string|undefined} locale
 * @returns {string}
 */
const formatCreatedAt = (iso, locale) => {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(ts);
  } catch {
    return '';
  }
};

const HEADER_TITLE_KEY = {
  pick: 'chat.hitl.pickTitle',
  choice: 'chat.hitl.choiceTitle',
};

/**
 * Chrome shared by every HITL gate mode: the Card frame and the identifying
 * header (feature title, mode + gate badges, raised-at timestamp,
 * collapse/expand, manual dismiss). Because the durable fallback can surface
 * a task from another (or terminated) run — pending tasks carry no
 * conversation uuid yet — the feature title + when the gate was raised let a
 * human recognize a foreign card at a glance before acting on it.
 *
 * The mode body renders as `children` and is hidden while collapsed
 * (Claude/Cursor-style), so a docked card can shrink to just its header while
 * a long thread or another card needs the room.
 *
 * @param {object} props
 * @param {{ mode?: string, gate?: string, createdAt?: string, render?: object }} props.card
 * @param {boolean} props.resolving  Disables the dismiss control mid-resolve.
 * @param {boolean} props.collapsed
 * @param {() => void} props.onToggleCollapse
 * @param {() => void} props.onDismiss
 * @param {string} [props.gapClass='gap-3']  Body spacing (choice mode uses gap-4).
 * @param {string} [props.translationNamespace='chat']
 */
function HitlGateCardShell({
  card,
  resolving,
  collapsed,
  onToggleCollapse,
  onDismiss,
  gapClass = 'gap-3',
  translationNamespace = 'chat',
  children,
}) {
  const { t, i18n } = useTranslation(translationNamespace);
  const featureTitle = deriveHitlFeatureTitle(card?.render ?? {});
  const createdAtLabel = formatCreatedAt(card?.createdAt, i18n?.language);
  const headerTitleKey = HEADER_TITLE_KEY[card?.mode] ?? 'chat.hitl.title';

  return (
    <Card className={`flex flex-col ${collapsed ? 'gap-0' : gapClass} p-4`}>
      <div className="flex flex-col gap-1">
        {featureTitle ? (
          <p className="truncate text-sm font-semibold text-content" title={featureTitle}>
            {featureTitle}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <Badge>{t(headerTitleKey)}</Badge>
          {card.gate ? <Badge>{card.gate}</Badge> : null}
          <div className="ml-auto flex items-center gap-1.5">
            {createdAtLabel ? (
              <span
                className="whitespace-nowrap text-[11px] text-content-secondary"
                title={createdAtLabel}
              >
                {createdAtLabel}
              </span>
            ) : null}
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
              aria-label={t(collapsed ? 'chat.hitl.expand' : 'chat.hitl.collapse')}
              title={t(collapsed ? 'chat.hitl.expand' : 'chat.hitl.collapse')}
              className="rounded p-1 text-content-muted transition-colors hover:bg-surface-hover hover:text-content"
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={resolving}
              aria-label={t('chat.hitl.dismiss')}
              title={t('chat.hitl.dismiss')}
              className="rounded p-1 text-content-muted transition-colors hover:bg-surface-hover hover:text-content disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {collapsed ? null : children}
    </Card>
  );
}

export default HitlGateCardShell;
